import { Controller } from "@hotwired/stimulus"

// TaxJar destination is zip / state / saved card. Street and city
// keystrokes must not hit Rebilly. Quote once per destination
// fingerprint (5-digit zip + state + country + card).
const DESTINATION_FIELDS = new Set([
  "payment_method_id",
  "state_code",
  "state_name",
  "zipcode",
  "country"
])

export default class extends Controller {
  static targets = ["lines", "total"]
  static values = { url: String }

  connect() {
    this.boundOnInput = this.onInput.bind(this)
    this.boundOnChange = this.onChange.bind(this)
    this.element.addEventListener("input", this.boundOnInput)
    this.element.addEventListener("change", this.boundOnChange)
    this.refresh()
  }

  disconnect() {
    this.element.removeEventListener("input", this.boundOnInput)
    this.element.removeEventListener("change", this.boundOnChange)
    clearTimeout(this.timeout)
    this.abortController?.abort()
  }

  onInput(event) {
    if (event?.target?.name !== "zipcode") return
    if (this.zipDigits(event.target.value).length < 5) return

    this.scheduleRefresh()
  }

  onChange(event) {
    if (event && !DESTINATION_FIELDS.has(event.target?.name)) return

    this.scheduleRefresh()
  }

  scheduleRefresh() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.refresh(), 800)
  }

  async refresh() {
    if (!this.urlValue) return

    const params = this.taxParams()
    if (!this.hasTaxDestination(params)) return

    const fingerprint = this.destinationFingerprint(params)
    if (fingerprint === this.lastFingerprint) return

    this.lastFingerprint = fingerprint
    this.abortController?.abort()
    this.abortController = new AbortController()

    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        signal: this.abortController.signal
      })
      if (!response.ok) return

      const data = await response.json()
      this.renderTaxes(data.taxes || [])
      if (this.hasTotalTarget && data.total) {
        this.totalTarget.textContent = data.total
      }
    } catch (error) {
      if (error.name === "AbortError") return

      this.lastFingerprint = null
      console.error("Checkout tax preview failed:", error)
    }
  }

  hasTaxDestination(params) {
    return this.destinationFingerprint(params).length > 0
  }

  destinationFingerprint(params) {
    const pm = params.get("payment_method_id") || ""
    const zip = this.zipDigits(params.get("zipcode")).slice(0, 5)
    const state = params.get("state_code") || params.get("state_name") || ""
    const country = params.get("country") || ""
    if (!pm && zip.length < 5 && !state) return ""

    return [pm, zip, state, country].join("|")
  }

  zipDigits(value) {
    return (value || "").replace(/\D/g, "")
  }

  taxParams() {
    const params = new URLSearchParams()
    const selected = this.element.querySelector(
      'input[name="payment_method_id"]:checked'
    )
    if (selected?.value && selected.value !== "new_payment") {
      params.set("payment_method_id", selected.value)
    }

    ;["address1", "address2", "city", "state_code", "state_name", "zipcode", "country"].forEach((name) => {
      const field = this.element.querySelector(`[name="${name}"]`)
      if (field?.value) params.set(name, field.value)
    })

    return params
  }

  renderTaxes(taxes) {
    if (!this.hasLinesTarget) return

    if (!taxes.length) {
      this.linesTarget.innerHTML = ""
      return
    }

    this.linesTarget.innerHTML = taxes.map((tax) => {
      const description = this.escape(tax.description)
      const amount = this.escape(tax.amount)
      return `<div style="display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-top: 6px;">
        <span style="font-size: 13.5px; color: #495057;">${description}</span>
        <span style="font-family: 'DIN Pro Cond', 'Arial Narrow', sans-serif; font-weight: 700; font-size: 16px; color: #171717; flex-shrink: 0;">${amount}</span>
      </div>`
    }).join("")
  }

  escape(value) {
    const node = document.createElement("div")
    node.textContent = value == null ? "" : String(value)
    return node.innerHTML
  }
}
